import { inject, nextTick, onUnmounted, watch, type Ref } from 'vue'
import { routeLocationKey, routerKey } from 'vue-router'

import {
  historyKey,
  positionOfState,
  readHistoryEntry,
  writeHistoryEntry,
} from '@/utils/historyMemory'

/**
 * Remember and restore one plain value per history entry, so back returns a screen
 * to the state the user left it in — the value-shaped sibling of
 * `useScrollRestore` (a scroll container) and `useMapViewportRestore` (a Leaflet
 * camera). See `src/utils/historyMemory.ts` for the keying and pruning rules.
 *
 * Reach for this when the state belongs to a widget with no scroll container of
 * its own: `useScrollRestore` can only carry state alongside an offset, and the
 * home map has no scroller at all.
 *
 * Unlike the map camera, there is no `moveend` to hook — the value is watched, so
 * every change is remembered as it happens, including a deliberate clear.
 */
export interface HistoryValue<T> {
  /**
   * Record `value` for the current entry explicitly, overriding what the watch
   * below has already stored.
   *
   * This exists for state a widget resets as part of *leaving* a screen — the stop
   * search clears its query when a suggestion is tapped, which is a step in
   * navigating away, not something the user did to the entry they are leaving.
   * Without this, back would restore the reset rather than what they had typed.
   */
  remember: (value: T) => void
}

/** Does nothing, for a widget mounted without a router installed. */
const INERT: HistoryValue<unknown> = { remember: () => {} }

export function useHistoryValue<T>(namespace: string, id: string, value: Ref<T>): HistoryValue<T> {
  /**
   * `inject` with an explicit default rather than `useRouter()` / `useRoute()`:
   * widgets that use this are mounted bare in their own unit suites, and a missing
   * router has to be a supported state rather than a crash.
   */
  const router = inject(routerKey, undefined)
  const route = inject(routeLocationKey, undefined)
  if (!router || !route) return INERT as HistoryValue<T>

  /** Never cached: both halves advance under the component during a navigation. */
  const currentKey = (): string =>
    historyKey(positionOfState(router.options.history.state), namespace, id, route.fullPath)

  function restore(): void {
    const remembered = readHistoryEntry<T>(currentKey())
    if (remembered !== undefined) value.value = remembered
  }

  /**
   * Restore BEFORE the watch is installed, and never with `immediate: true`. The
   * watch writes on every change, so an immediate first run would store the
   * widget's freshly-mounted default — an empty string — over the value being
   * restored, and the restore below would then read back what it had just erased.
   */
  restore()

  /**
   * Every value is stored, including the default: a field the user deliberately
   * cleared has to come back cleared, not repopulated with the pre-clear text.
   * That is why "nothing stored" and "stored as empty" must stay distinguishable,
   * and why `restore()` tests against `undefined` rather than falsiness.
   */
  /**
   * `flush: 'sync'` so writes land in assignment order. A caller that resets the
   * value and then calls `remember()` with the pre-reset value — which is exactly
   * what tapping a search suggestion does — would otherwise have the deferred watch
   * callback run *after* `remember()` and overwrite it with the reset. The cost is
   * one `Map` write per keystroke.
   */
  watch(value, (next) => writeHistoryEntry(currentKey(), next), { flush: 'sync' })

  // Also restore after every confirmed navigation, for the case where RouterView
  // reuses the component instance and nothing remounts.
  const stopAfterEach = router.afterEach(() => void nextTick(restore))
  onUnmounted(stopAfterEach)

  return { remember: (next) => writeHistoryEntry(currentKey(), next) }
}
